import {
  Avatar,
  Button,
  Badge,
  IconButton,
  Dialog,
  Card,
  Grid,
  CircularProgress,
  LinearProgress,
} from '@material-ui/core';
import {
  createExternalBusiness,
  getExternalBusinessDataFromSiret,
} from '@webapps/business';
import {
  FormikTextFieldPx,
  useAuth,
  useMutation,
  useToast,
} from '@webapps/reactjs-tech-components';
import { AddRounded } from '@material-ui/icons';
import { useStyles } from '@webapps/styles';;
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import * as Yup from 'yup';
import CropEasy from 'components/crop/CropEasy';

import PhoneInput, {
  isValidPhoneNumber,
} from 'react-phone-number-input';
import { FormattedMessage } from 'react-intl';

const AddExternalPartner = ({ onClose, refetch }) => {
  const { idUser } = useAuth();

  const [data, setData] = useState({
    businessExternal: {
      name: "",
      email: "",
      phone: "",
      siret: "",
      school: {
        id: idUser,
      },
      sector: {
        id: "",
      },
      address: {
        label: "",
        city: "",
        cityCode: "",
      },
    },
    picture: null,
  });

  const [currentStep, setCurrentStep] = useState(0);

  const {
    error,
    isLoading,
    isSuccess,
    mutateAsync: mutateCreateExternalBusiness,
  } = useMutation(createExternalBusiness);

  const { addToast } = useToast();

  useEffect(() => {
    if (error) {
      addToast({
        status: 'error',
        message: error?.response?.data?.description || 'Erreur pour ajouter un partenaire externe',
      });
    }
    if (isSuccess) {
      addToast({
        status: 'success',
        message: 'Partenaire externe ajouté avec succès',
      });
      onClose();
      refetch();
    }
  }, [error, isSuccess]);

  const makeRequest = async (values) => {
    const formData = new FormData();
    formData.append('businessExternal', new Blob([JSON.stringify(values.businessExternal)], { type: 'application/json' }));
    formData.append('picture', values.picture);
  
    await mutateCreateExternalBusiness({formData});
  };

  const handleNextStep = (newData, final = false) => {
    setData((prev) => ({ ...prev, ...newData }));

    if (final) {
      makeRequest(newData);
      return;
    }

    setCurrentStep((prev) => prev + 1);
  };

  const handlePrevStep = (newData) => {
    setData((prev) => ({ ...prev, ...newData }));
    setCurrentStep((prev) => prev - 1);
  };

  const steps = [
    <StepOne key={1} data={data} next={handleNextStep} />,
    <StepTwo key={2} data={data} next={handleNextStep} prev={handlePrevStep} isLoading={isLoading} />,
  ];

  return steps[currentStep];
};

AddExternalPartner.propTypes = {
  onClose: PropTypes.func,
  refetch: PropTypes.func,
};

const StepOne = ({ data, next }) => {
  const {
    css,
    classes,
    global,
    cx,
    theme: { spacing },
  } = useStyles(styles);

  const [error, setError] = useState(null);

  const formik = useFormik({
    initialValues: data,
    enableReinitialize: true,
    onSubmit: async (values) => {
      next(values);
    }
  });

  const {
    isLoading,
    mutate: getInfoFromSiret,
  } = useMutation(getExternalBusinessDataFromSiret, {
    onSuccess: (result) => {
    const etablissement = result.data.etablissement;
      formik.setFieldValue('businessExternal.name', etablissement?.uniteLegale?.denominationUniteLegale)
      formik.setFieldValue('businessExternal.sector.id', etablissement?.uniteLegale?.activitePrincipaleUniteLegale?.substring(0, 2))
      formik.setFieldValue('businessExternal.address.label', `${etablissement?.adresseEtablissement?.numeroVoieEtablissement} ${etablissement?.adresseEtablissement?.typeVoieEtablissement} ${etablissement?.adresseEtablissement?.libelleVoieEtablissement}`)
      formik.setFieldValue('businessExternal.address.city', etablissement?.adresseEtablissement?.libelleCommuneEtablissement)
      formik.setFieldValue('businessExternal.address.cityCode', etablissement?.adresseEtablissement?.codePostalEtablissement)
      
      formik.handleSubmit();
    },
    onError: (error) => {
      const status = error.response.status;
      switch (status) {
        case 400:
          setError('Le SIRET est incorrect');
          break;
        case 401:
          setError('Nous rencontrons un problème de connexion, veuillez réessayer plus tard');
          break;
        case 404:
          setError(error.response.data.header.message);
          break;
        case 405:
          setError('Le service est momentanément indisponible, veuillez réessayer plus tard');
          break;
        case 429:
          setError('Vous avez atteint le nombre maximum de requêtes, veuillez réessayer plus tard');
          break;
        case 500: 
          setError('Une erreur indépendante de notre volonté lié au service de l\'état est survenue, veuillez réessayer plus tard');
          break;
        case 503:
          setError('Le service est momentanément indisponible, veuillez réessayer plus tard');
          break;
        default:
          setError('Une erreur est survenue, veuillez réessayer plus tard');
          break;
      }
    },
  });

  useEffect(() => {
    let timeoutId;

    if (isLoading) {
      timeoutId = setTimeout(() => {
        if (isLoading) {
          setError('Cela prend plus de temps que prévu...');
        }
      }, 10000);
    }
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isLoading]);

  const handleSiret = async (searchValue) => {
    if(searchValue.target.value.length > 14) return;

    const integerRegex = /^\d+$/;

    formik.setFieldValue('businessExternal.siret', searchValue.target.value);

    if (!integerRegex.test(searchValue.target.value)) return;

    if(searchValue.target.value.length === 14){
      setError(null);
      await getInfoFromSiret({ id: searchValue.target.value });
    }
  };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '@media (max-width:768px)': { margin: spacing(1) }
      })}
    >
      <Grid container xs={12}>
        <Grid className={css({ marginTop: spacing(2) })} container>
          <Grid item xs={12}>
            <FormikTextFieldPx
              defaultValue=""
              disabled={isLoading}
              borderRadius={6}
              formik={formik}
              fullWidth
              onChange={(value) => handleSiret(value)}
              label="Siret"
              name="businessExternal.siret"
            />
          </Grid>
          {isLoading && (
            <div style={{width: '100%'}}>
              <LinearProgress />
            </div>
          )}
          {error && (
            <Grid item xs={12} className={css({marginTop: '10px'})}>
              <div className={classes.countAspectPassed}>{error}</div>
            </Grid>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

StepOne.propTypes = {
  data: PropTypes.object,
  next: PropTypes.func,
};


const StepTwo = ({ data, next, prev, isLoading }) => {
  const {
    css,
    classes,
    global,
    cx,
    theme: { spacing },
  } = useStyles(styles);

  const [errorPhone, setErrorPhone] = useState(false);
  const [openCrop, setOpenCrop] = useState(false);

  const [phoneClasses, setPhoneClasses] = useState('phoneInput');
  const phoneClassesRef = useRef();
  phoneClassesRef.current = phoneClasses;

  const handleChangePhone = (phoneInfo) => {
    if (phoneInfo) {
      if (isValidPhoneNumber(phoneInfo)) {
        formik.setFieldValue('businessExternal.phone', phoneInfo);
        setErrorPhone(false);
        setPhoneClasses('phoneInput');
      } else {
        setErrorPhone(true);
        setPhoneClasses('phoneInputError');
      }
    }
  };

  const formik = useFormik({
    initialValues: data,
    enableReinitialize: true,
    onSubmit: async (values) => next(values, true),
  });

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '@media (max-width:768px)': { margin: spacing(1) }
      })}
    >
      <Grid container xs={12}>
        <Grid item xs={2} className={css({ marginTop: spacing(3) })}>
          <Card
            classes={{
              root: css({
                overflow: 'visible',
              }),
            }}
            elevation={0}
          >
            <Badge
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              className={
                css({
                    top: -16
                })
              }
              badgeContent={
                <IconButton
                  className={css({
                    background: '#F3F7FF',
                    border: '1px solid #C0D9FF',
                    width: '24px',
                    height: '24px',
                    '&:hover': {
                      backgroundColor: '#F3F7FF',
                    },
                  })}
                  onClick={() => setOpenCrop(true)}
                  size="small"
                >
                  <AddRounded
                    style={{ color: '#5A6DA1', fontSize: '15.0px' }}
                  />
                </IconButton>
              }
              overlap="circular"
            >
              {formik?.values?.picture ? (
                <Avatar className={classes.avatar} src={URL.createObjectURL(formik?.values?.picture)} />
              ) : (
                <Avatar className={classes.avatar}>
                  {formik.values.businessExternal.name?.substr(0, 1)}
                </Avatar>
              )}
            </Badge>
          </Card>
        </Grid>
        <Grid item className={css({ marginTop: spacing(3) })} xs={10}>
          <FormikTextFieldPx
            defaultValue=""
            disabled
            formik={formik}
            fullWidth
            borderRadius={6}
            label="Nom de l'entreprise"
            name="businessExternal.name"
          />
        </Grid>
        <Grid container>
          <Grid item xs={12}>
            <FormikTextFieldPx
              defaultValue=""
              formik={formik}
              fullWidth
              borderRadius={6}
              label="Email de l'entreprise"
              name="businessExternal.email"
            />
          </Grid>
        </Grid>
        <Grid className={css({ marginTop: spacing(3) })} container>
          <Grid className={classes.phoneInputContainer} item xs={12}>
            <PhoneInput
              className={classes[phoneClassesRef.current]}
              countryCallingCodeEditable={false}
              defaultCountry="FR"
              international
              limitMaxLength
              name="businessExternal.phone"
              onChange={(phone) => {
                handleChangePhone(phone);
              }}
              placeholder={<FormattedMessage id="createUserForm.phone" />}
              value={formik.values.businessExternal.phone}
            />
          </Grid>
          <div className={classes.phoneError}>
            <div />
            <div>
              {errorPhone && (
                <div className={css({ color: 'red' })}>
                  Le numéro n'est pas correct
                </div>
              )}
            </div>
          </div>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={classes.button}
            color="primary"
            fullWidth
            onClick={() => prev(formik.values)}
            variant="outlined"
          >
            Retour
          </Button>
        </Grid>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={classes.button}
            color="primary"
            disabled={isLoading}
            fullWidth
            onClick={formik.handleSubmit}
            type="submit"
            variant="contained"
          >
            Ajouter
          </Button>
        </Grid>
      </Grid>
      <Dialog
        classes={{
          paperScrollPaper: css({
            '@media screen and (max-width: 768px)': {
              margin: '10px',
              height: '60vh',
            },
            width: '100%',
          }),
        }}
        onClose={() => setOpenCrop(false)}
        open={openCrop}
      >
        <CropEasy
          {...{
            photoURL: formik?.values?.picture ? URL.createObjectURL(formik?.values?.picture) : null,
            setOpenCrop: setOpenCrop,
            type: 'businessExternal',
            data: formik.values,
          }}
        />
      </Dialog>
    </div>
  );
};

StepTwo.propTypes = {
  data: PropTypes.object,
  next: PropTypes.func,
  prev: PropTypes.func,
};

const styles = ({ spacing }) => ({
  countAspect: {
    color: 'black',
  },
  countAspectPassed: {
    color: 'red',
    textAlign: 'center',
  },
  button: {
    borderRadius: 20,
  },
  phoneInputContainer: {
    height: '50px',
  },
  avatar: {
    height: 80,
    width: 80,
  },
  phoneInput: {
    height: '100%',
    '& > *': {
      backgroundColor: 'rgba(0,0,0,0)',
      border: '1px solid #C0C0C0',
      borderRadius: 3,
      padding: spacing(0, 2),
      height: '100%',
      color: '#737373',
      '&:hover': {
        border: '1px solid black',
      },
      '&:focus': {
        border: '1px solid #0060F9',
        color: 'black',
      },
    },
  },
  phoneInputError: {
    height: '100%',
    '& > *': {
      backgroundColor: 'rgba(0,0,0,0)',
      border: '1px solid red',
      borderRadius: 3,
      padding: spacing(0, 2),
      height: '100%',
      color: '#737373',
      '&:hover': {
        border: '1px solid black',
      },
      '&:focus': {
        border: '1px solid #0060F9',
        color: 'black',
      },
    },
  },
});

export default AddExternalPartner;
